import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import ToggleButton from '../../layout/ReusableComponent/Buttons/ToggleButton';

export default function ProductsAccountMenu({
  profile,
  avatarSrc,
  onAvatarError,
  onSignOut,
  signOutBusy = false,
  compact = false,
  isDarkTheme = false,
  onDarkThemeToggle,
}) {
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState({ top: 0, right: 0 });
  const triggerRef = React.useRef(null);
  const dropdownRef = React.useRef(null);

  const updateMenuPosition = React.useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    setMenuPos({
      top: Math.round(rect.bottom + 6),
      right: Math.max(0, Math.round(window.innerWidth - rect.right)),
    });
  }, []);

  React.useEffect(() => {
    if (!open) return undefined;
    updateMenuPosition();
    const onReposition = () => updateMenuPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, updateMenuPosition]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
    };
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) {
        requestAnimationFrame(() => updateMenuPosition());
      }
      return next;
    });
  };

  const menuPanel = (
    <div
      ref={dropdownRef}
      className={`products-account-dropdown products-account-dropdown--portal${
        compact ? ' products-account-dropdown--portal-compact' : ''
      }`}
      role="menu"
      aria-label="Account options"
      style={{ top: menuPos.top, right: menuPos.right }}
    >
      {compact ? (
        <div className="products-account-dropdown-identity">
          <span className="products-account-dropdown-name">{profile.name || 'Account'}</span>
          {profile.email ? (
            <span className="products-account-dropdown-email">{profile.email}</span>
          ) : null}
        </div>
      ) : null}
      <div
        className="products-account-dropdown-theme"
        role="presentation"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <span className="products-account-dropdown-theme-label" id="products-account-theme-label">
          Dark theme
        </span>
        <ToggleButton
          className="products-account-theme-toggle"
          checked={isDarkTheme}
          onToggle={onDarkThemeToggle}
        />
      </div>
      <Link
        to="/d/profile"
        className="products-account-dropdown-item"
        role="menuitem"
        onClick={() => setOpen(false)}
      >
        Manage profile
      </Link>
      <button
        type="button"
        className="products-account-dropdown-item products-account-dropdown-item--danger"
        role="menuitem"
        disabled={signOutBusy}
        onClick={() => {
          setOpen(false);
          onSignOut();
        }}
      >
        {signOutBusy ? 'Signing out…' : 'Sign out'}
      </button>
    </div>
  );

  const menuLayer =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div className="products-account-menu-layer" data-products-account-menu-layer>
            {menuPanel}
          </div>,
          document.querySelector('.products-page') || document.body
        )
      : null;

  return (
    <div className={`products-account-menu${compact ? ' products-account-menu--compact' : ''}`}>
      <button
        ref={triggerRef}
        type="button"
        className={`products-account-trigger${open ? ' products-account-trigger--open' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account menu"
      >
        <img
          src={avatarSrc}
          alt=""
          className="products-account-avatar"
          onError={(e) =>
            onAvatarError(e.currentTarget, {
              userId: profile.userId,
              name: profile.name,
            })
          }
        />
        {!compact ? (
          <span className="products-account-meta">
            <span className="products-account-name">{profile.name || 'Account'}</span>
            {profile.email ? <span className="products-account-email">{profile.email}</span> : null}
          </span>
        ) : null}
        <FiChevronDown className="products-account-chevron" size={16} strokeWidth={2} aria-hidden />
      </button>
      {menuLayer}
    </div>
  );
}
